<script lang="ts">
import type { CommandPaletteEmits, CommandPaletteGroup, CommandPaletteItem, CommandPaletteProps, CommandPaletteSlots, PopoverEmits, PopoverProps, PopoverSlots } from '#ui/types';

export interface CommandPopoverProps<T extends CommandPaletteItem, G extends CommandPaletteGroup<T>> extends CommandPaletteProps<G, T>, Pick<PopoverProps, 'open'> {
	popover?: Omit<Partial<PopoverProps>, 'open'>;
	content?: PopoverProps['content'];
	canLoadMore?: boolean; // Opts into infinite scroll
}

export type CommandPopoverSlots<T extends CommandPaletteItem, G extends CommandPaletteGroup<T>> = CommandPaletteSlots<T, G> & {
	'default': PopoverSlots['default'];
	'anchor': PopoverSlots['anchor'];
	'content-top': () => any;
	'content-bottom': () => any;
};
</script>

<script setup lang="ts" generic="T extends CommandPaletteItem, G extends CommandPaletteGroup<T>">
import { reactiveOmit, useInfiniteScroll, useMutationObserver, useResizeObserver } from '@vueuse/core';
import { useForwardPropsEmits } from 'reka-ui';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';

import { omit, pick } from '#layers/base/public';
import { mergeUi } from '#layers/ui/utils/merge-ui';

const props = withDefaults(defineProps<CommandPopoverProps<T, G>>(), {
	content: () => ({ side: 'bottom', align: 'start' }),
	canLoadMore: false,
});

// `CommandPaletteEmits` omits the `searchTerm` model's update event, so it must be declared here for
// `useForwardPropsEmits` to re-emit it — otherwise `v-model:search-term` on a consumer never fires.
const emits = defineEmits<CommandPaletteEmits<T> & { 'update:searchTerm': [value: string]; 'loadMore': [] } & Pick<PopoverEmits, 'update:open'>>();

const slots = defineSlots<CommandPopoverSlots<T, G>>();

const popoverSlotNames = ['default', 'anchor'] as const;

/** How close to the end of the list scrolling for the next page to be requested. */
const LOAD_MORE_DISTANCE_PX = 50;

const open = defineModel<boolean>('open');

const popoverProps = computed(() => ({
	...props.popover,
	// Default the content panel radius; consumers' own popover.ui merges on top additively.
	ui: mergeUi({ content: 'rounded-[0.625rem]' }, props.popover?.ui),
}));

const commandPaletteForwarded = useForwardPropsEmits(reactiveOmit(props, 'popover', 'content', 'open', 'canLoadMore'), emits);

const wrapperRef = useTemplateRef('wrapper');
const scroller = ref<HTMLElement>();

useDynamicListBoxHeight();

useInfiniteScroll(scroller, () => emits('loadMore'), {
	distance: LOAD_MORE_DISTANCE_PX,
	canLoadMore: () => props.canLoadMore,
});

function useDynamicListBoxHeight() {
	const inner = ref<HTMLElement>();

	function syncHeight() {
		const rootEl = wrapperRef.value?.querySelector('.command-palette-root');
		const contentEl = rootEl?.querySelector('[data-slot="content"]') as HTMLElement | null;
		const innerEl = contentEl?.firstElementChild as HTMLElement | null;

		scroller.value = contentEl ?? undefined;
		inner.value = innerEl ?? undefined;
		if (!contentEl || !innerEl) return;

		contentEl.style.setProperty('--cmd-list-height', `${innerEl.offsetHeight.toFixed(0)}px`);
	}

	let pending = 0;

	function scheduleSync() {
		if (pending) return;

		pending = requestAnimationFrame(() => {
			pending = 0;
			syncHeight();
		});
	}

	useMutationObserver(wrapperRef, scheduleSync, { childList: true, subtree: true });
	useResizeObserver(inner, scheduleSync);
	watch(wrapperRef, () => scheduleSync());
	onMounted(scheduleSync);
}
</script>

<template>
	<DPopover v-model:open="open" :content v-bind="popoverProps">
		<template v-for="(_, name) in pick(slots, popoverSlotNames)" #[name]="slotData: any">
			<slot :name v-bind="slotData" />
		</template>

		<template #content>
			<div ref="wrapper" class="flex max-h-(--reka-popper-available-height) flex-col">
				<slot name="content-top" />
				<DCommandPalette
					v-bind="commandPaletteForwarded"
					:ui="mergeUi({
						root: 'command-palette-root p-0',
						// The list scrolls vertically only; `overscroll-none` keeps a trackpad gesture from
						// elastically bouncing the panel contents when there is nothing left to scroll.
						content: 'h-(--cmd-list-height) motion-safe:transition-height duration-75 overflow-x-hidden overflow-y-auto overscroll-none',
						viewport: 'flex-none',
						input: '[&>input]:py-2.5 [&>input]:px-4 [&>input]:h-9.5 [&>span]:hidden [&>input]:truncate',
						group: 'p-2 flex flex-col gap-0.5',
						item: 'px-2 py-1.5 text-sm',
						itemLeadingIcon: 'size-4',
						label: 'py-1 px-2',
						empty: 'py-2.5 text-start px-3',
					} as CommandPopoverProps<T, G>['ui'], props.ui)"
				>
					<template v-for="(_, name) in omit(slots, popoverSlotNames)" #[name]="slotData: any">
						<slot :name v-bind="slotData" />
					</template>
				</DCommandPalette>
				<slot name="content-bottom" />
			</div>
		</template>
	</DPopover>
</template>
